import {makeItemsListUrl} from '@frontend/helpers'
import {findCategoryById} from '@frontend/helpers/findCategoryById'
import type {
    AdCategory,
    AdLocation,
    AdLocations,
    FilteredItemsPageUrlQueryKnownParams,
} from '@frontend/modules/public/ads/AdsModuleTypes'
import {CacheService} from '@frontend/services/CacheService'
import {create} from 'zustand/react'
import {PublicDataApi} from '../api/PublicDataApi'
import {LocaleService} from '../services/LocaleService'

// Тип состояния хранилища.
export interface AdsStoreState {
    // Данные:

    // Древовидный список категорий.
    categories: AdCategory[]

    // Состояние загрузки категорий.
    loadingCategories: boolean

    // Древовидный список локаций.
    locations: AdLocations

    // Состояние загрузки локаций.
    loadingLocations: boolean

    // Видимость панели поиска объявлений.
    searchPanel: boolean

    // Разрешено ли взаимодействие с панелью поиска?
    isSearchPanelDisabled: boolean

    // Категория, выбранная в панели поиска.
    searchPanelCategory: AdCategory | null

    // Локация, выбранная в панели поиска.
    searchPanelLocation: AdLocation | null | undefined

    // Ключевые слова, веденные в панели поиска.
    searchPanelKeywords: string

    // API:

    // Генератор URL к странице со списком объявлений.
    // По умолчанию используются значения:
    // searchPanelCategory, searchPanelLocation, searchPanelKeywords
    makeItemsListUrl: (
        paramsAndArgs?: AdsStoreStateGetAdsListUrlParams
    ) => string

    // Загрузка списка категорий объявлений.
    loadCategories: (
        silent?: boolean,
        abortController?: AbortController
    ) => Promise<AdCategory[]>

    // Загрузка списка локаций.
    loadLocations: (
        silent?: boolean,
        onLocationsLoaded?: (
            locations: AdLocations,
            searchPanelLocation: AdsStoreState['searchPanelLocation']
        ) => AdsStoreState['searchPanelLocation'],
        abortController?: AbortController
    ) => Promise<AdLocations>

    // Сохранение списка категорий.
    setCategories: (categories: AdCategory[]) => void

    // Изменение состояния загрузки списка категорий.
    setIsLoadingCategories: (enable: boolean) => void

    // Сохранение списка локаций.
    setLocations: (locations: AdLocations) => void

    // Изменение состояния загрузки списка локаций.
    setIsLoadingLocations: (enable: boolean) => void

    // Задать видимость панели поиска объявлений.
    setSearchPanelVisibility: (visible: boolean) => void

    // Задать категорию, выбранную в панели поиска.
    // Если null, то не нужно фильтровать по категории (страница "Все объявления").
    setSearchPanelCategory: (category: AdCategory | null) => void

    // Найти и задать категорию по ID.
    // Если categoryId <= 0, то не нужно фильтровать по категории (страница "Все объявления").
    setSearchPanelCategoryById: (categoryId?: number | null) => void

    // Задать локацию, выбранную в панели поиска.
    // Если null, то не нужно фильтровать по локации (опция "Все локации").
    setSearchPanelLocation: (location: AdLocation | null) => void

    // Задать ключевые слова, веденные в панели поиска.
    setSearchPanelKeywords: (keywords: string) => void

    // Разрешить/запретить взаимодействие с панелью поиска.
    setIsSearchPanelDisabled: (disabled: boolean) => void
}

interface AdsStoreStateGetAdsListUrlParams extends FilteredItemsPageUrlQueryKnownParams {
    category?: AdCategory | null
}

// Хранилище данных и состояний для объявлений.
export const useAdsStore = create<AdsStoreState>(
    (set, get) => ({
        categories: [],
        loadingCategories: true,
        locations: {levels: [], tree: []},
        loadingLocations: true,
        searchPanel: true,
        isSearchPanelDisabled: true,
        searchPanelCategory: null,
        searchPanelLocation: undefined,
        searchPanelKeywords: '',

        // API:

        makeItemsListUrl(paramsAndArgs: AdsStoreStateGetAdsListUrlParams = {}): string {
            const {
                category = get().searchPanelCategory,
                location = get().searchPanelLocation?.slugWithId,
                keywords = get().searchPanelKeywords,
                ...otherParams
            } = paramsAndArgs
            return makeItemsListUrl(category, {
                location: location ? String(location) : undefined,
                keywords,
                ...otherParams,
            })
        },

        async loadCategories(
            silent: boolean = false,
            abortController?: AbortController
        ): Promise<AdCategory[]> {
            return CacheService.rememberToStorage(
                'categories',
                async () => {
                    set({
                        loadingCategories: !silent,
                    })
                    return PublicDataApi.getCategoriesTree(abortController)
                },
                1440,
                LocaleService.primaryRegion.region
            )
                .then(categories => {
                    set({
                        loadingCategories: false,
                        categories: categories ?? [],
                    })
                    return categories
                })
        },

        async loadLocations(
            silent: boolean = false,
            onLocationsLoaded?: (
                locations: AdLocations,
                searchPanelLocation: AdsStoreState['searchPanelLocation']
            ) => AdsStoreState['searchPanelLocation'],
            abortController?: AbortController
        ): Promise<AdLocations> {
            return CacheService.rememberToStorage(
                'locations',
                async () => {
                    set({
                        loadingLocations: !silent,
                    })
                    return PublicDataApi.getLocationsTree(abortController)
                        .then(responseData => {
                            const locations: AdLocations = {
                                levels: responseData.levels || [],
                                tree: responseData.locations || [],
                            }
                            return locations
                        })
                },
                1440,
                LocaleService.primaryRegion.region
            )
                .then(locations => {
                    set({
                        loadingLocations: false,
                        locations: locations ?? {levels: [], tree: []},
                        searchPanelLocation: (
                            onLocationsLoaded
                                ? onLocationsLoaded(locations, get().searchPanelLocation)
                                : get().searchPanelLocation
                        ),
                    })
                    return locations
                })
        },

        setCategories(categories: AdCategory[]): void {
            set({
                categories,
            })
        },

        setIsLoadingCategories(enable: boolean): void {
            set({
                loadingCategories: enable,
            })
        },

        setLocations(locations: AdLocations): void {
            set({
                locations,
            })
        },

        setIsLoadingLocations(enable: boolean): void {
            set({
                loadingLocations: enable,
            })
        },

        setSearchPanelVisibility(visible: boolean): void {
            set({
                searchPanel: visible,
            })
        },

        setSearchPanelCategory(category: AdCategory | null): void {
            set({
                searchPanelCategory: category,
            })
        },

        setSearchPanelCategoryById(categoryId?: number | null): void {
            set({
                searchPanelCategory: findCategoryById(get().categories, categoryId ?? null),
            })
        },

        setSearchPanelLocation(location: AdLocation | null): void {
            set({
                searchPanelLocation: location,
            })
        },

        setSearchPanelKeywords(keywords: string): void {
            set({
                searchPanelKeywords: keywords,
            })
        },

        setIsSearchPanelDisabled(disabled: boolean): void {
            set({
                isSearchPanelDisabled: disabled,
            })
        },
    })
)
